import { useApiToast } from 'components/Toaster';
import { getDatalists } from 'features/datalist';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetPersonProjectEventsMutation, useUpdatePersonProjectEventMutation } from 'services/person/event/event.endpoint';
import { PersonProjectEvent, PersonProjectEventPayload } from 'services/person/event/event.types';
import { Address } from 'services/person/person.types';
import { Document } from 'services/document/document.types';
import { Person } from 'services/person/person.types';

export default function useProjectEvolutionTabVM(person: Person) {
    const [activeTab, setActiveTab] = useState(0);

    const [events, setEvents] = useState<PersonProjectEvent[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<PersonProjectEvent>();

    const { datalists } = useSelector(getDatalists);

    const [timelineStep, setTimelineStep] = useState(0);
    const [timelineMaxSteps, setTimelineMaxSteps] = useState(0);
    const [emptyEventsCount, setEmptyEventsCount] = useState(0);

    const eventTimelineMaxCount = 5;

    const { personId } = useParams();
    const projectId = person?.projects[0].id;

    const { showErrorToast, showApiToast } = useApiToast();

    const fillerEvent: PersonProjectEvent = {
        reference: '',
        stakeholders_list: [],
        assistants_list: [],
        id: -1,
        label: '',
        communication_type: '',
        address: {} as Address,
        observation: '',
        documents: [],
        created_at: '',
        started_at: '',
        partners_list: [],
        emails_list: []
    } as PersonProjectEvent;

    const [
        getPersonProjectEvents,
        {
            isLoading: isGetPersonProjectEventsLoading,
            isError: isGetPersonProjectEventsError,
            isSuccess: isGetPersonProjectEventsSuccess,
            error: projectEventsError,
            data: projectEventsResponse
        }
    ] = useGetPersonProjectEventsMutation();

    const [
        updatePersonProjectEvent,
        {
            isLoading: isUpdateLoading,
            isError: isUpdateError,
            isSuccess: isUpdateSuccess,
            error: projectEventUpdateError,
            data: projectEventUpdateResponse
        }
    ] = useUpdatePersonProjectEventMutation();

    useEffect(() => {
        if (isUpdateLoading) return;
        if (isUpdateError && projectEventUpdateError) {
            showErrorToast(projectEventUpdateError, {
                message: `Erreur lors de la mise à jour de l'événement`
            });
        }
        if (isUpdateSuccess && projectEventUpdateResponse) {
            showApiToast(projectEventUpdateResponse, {
                successMessage: `Événement mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour de l'événement`
            });
            return;
        }
    }, [isUpdateLoading, isUpdateError, projectEventUpdateError, projectEventUpdateResponse, isUpdateSuccess, showErrorToast, showApiToast]);


    useEffect(() => {
        if (personId) {
            getPersonProjectEvents({ id: projectId });
        }
    }, [personId, getPersonProjectEvents]);

    useEffect(() => {
        if (isGetPersonProjectEventsLoading) return;
        if (isGetPersonProjectEventsError && projectEventsError) {
            showErrorToast(projectEventsError, {
                message: 'Erreur de chargement des évènements du projet'
            });
            return;
        }
        if (isGetPersonProjectEventsSuccess && projectEventsResponse) {
            const newEvents = projectEventsResponse.data;
            setEvents(newEvents);

            let emptyEvents = newEvents.length === eventTimelineMaxCount ? 0 : eventTimelineMaxCount - newEvents.length % eventTimelineMaxCount;
            setTimelineMaxSteps((newEvents.length + emptyEvents) / eventTimelineMaxCount);
            setEmptyEventsCount(emptyEvents);

            if (newEvents && newEvents.length) {
                setSelectedEvent(newEvents[0]);
            }
        }
    }, [isGetPersonProjectEventsLoading, isGetPersonProjectEventsError, isGetPersonProjectEventsSuccess, projectEventsError, projectEventsResponse, showErrorToast, eventTimelineMaxCount]);


    const updateEvent = async (event: PersonProjectEvent) => {
        //update events
        const updatedEvents = events.map((e) => e.id === event.id ? event : e);
        setEvents(updatedEvents);

        await updatePersonProjectEvent({
            eventId: event.id ?? 0,
            projectId: projectId ?? 0,
            body: {
                ...event,
                stakeholders: event.stakeholders_list.map((el) => parseInt(el.value)),
                assistants: event.assistants_list.map(el => parseInt(el.value)),
                partners: event.partners_list.map(el => parseInt(el.value)),
                emails: event.emails_list.map(el => el.value),
            }
        })

    }

    const handleFileAdded = (document: Document) => {
        if (!selectedEvent) return;
        const updatedEvent = { ...selectedEvent, documents: [...selectedEvent.documents, document] };
        setSelectedEvent(updatedEvent);
        updateEvent(updatedEvent);
    };

    const handleFileUpdated = (document: Document) => {
        if (!selectedEvent) return;
        const updatedEvent = { ...selectedEvent, documents: selectedEvent.documents.map((doc) => doc.id === document.id ? document : doc) };
        setSelectedEvent(updatedEvent);
        updateEvent(updatedEvent);
    };

    const handleFileDeleted = (file: Document) => {
        if (!selectedEvent) return;
        const updatedEvent = { ...selectedEvent, documents: selectedEvent.documents.filter((doc) => doc.id !== file.id) };
        setSelectedEvent(updatedEvent);
        updateEvent(updatedEvent);
    };



    return {
        personId,
        projectId,
        isGetPersonProjectEventsLoading,
        fillerEvent,
        activeTab,
        setActiveTab,
        events,
        eventTimelineMaxCount,
        emptyEventsCount,
        datalists,
        timelineMaxSteps,
        timelineStep,
        handleFileDeleted,
        selectedEvent,
        setTimelineStep,
        setSelectedEvent,
        handleFileAdded,
        handleFileUpdated
    };
}

